import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  instructorDraftCoursesAPI,
  getAllCoursesAPI,
  instructorCoursesAPI,
  instructorUnpublishCourseAPI,
  instructorDeclineCourseAPI,
} from "../../../redux/features/intructorSlice";
import DashboardTopSearch from "../../../components/DashboardTopSearch/DashboardTopSearch";
import TabFilterCourse from "../../../components/TabFilterCourse/TabFilterCourse";
import TabFilterContentCourse from "../../../components/TabFilterContentCourse/TabFilterContentCourse";
import InstructorNotification from "../../../components/Notification/InstructorNotification";

// Define course categories outside the component to avoid redeclaring
const COURSE_CATEGORIES = [
  "All Courses",
  "Assigned Courses",
  "Draft Courses",
  "Unpublished Courses",
  "Declined Courses",
];

export default function InstructorCourses() {
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [allCourses, setAllCourses] = useState([]);
  const [instructorCourses, setInstructorCourses] = useState([]);
  const [instructorDraftCourses, setInstructorDraftCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState("Assigned Courses");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [instructorUnpublisCourses, setInstructorUnpublisCourses] = useState(
    []
  );
  const [instructorDeclineCourses, setInstructorDeclineCourses] = useState([]);

  // Fetch all courses and instructor courses concurrently
  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [
          allCoursesResult,
          instructorCoursesResult,
          instructorDraftCoursesResult,
          instructorUnpublishCourseResult,
          instructorDeclineCourseResult,
        ] = await Promise.all([
          dispatch(getAllCoursesAPI({ token: auth.token })).unwrap(),
          dispatch(instructorCoursesAPI({ token: auth.token })).unwrap(),
          dispatch(instructorDraftCoursesAPI({ token: auth.token })).unwrap(),
          dispatch(
            instructorUnpublishCourseAPI({ token: auth.token })
          ).unwrap(),
          dispatch(instructorDeclineCourseAPI({ token: auth.token })).unwrap(),
        ]);

        if (allCoursesResult.success) {
          setAllCourses(allCoursesResult.data);
        }
        if (instructorCoursesResult.success) {
          setInstructorCourses(instructorCoursesResult.data);
        }
        if (instructorDraftCoursesResult.success) {
          setInstructorDraftCourses(instructorDraftCoursesResult.data);
        }
        if (instructorUnpublishCourseResult.success) {
          setInstructorUnpublisCourses(instructorUnpublishCourseResult.data);
        }
        if (instructorDeclineCourseResult.success) {
          setInstructorDeclineCourses(instructorDeclineCourseResult.data);
        }
      } catch (error) {
        setError("Failed to fetch courses. Please try again.");
        console.error("Failed to fetch courses:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourses();
  }, [auth.token, dispatch]);

  // Derive filtered courses based on activeCourse
  const filteredCard = useMemo(() => {
    if (activeCourse === "All Courses") {
      return allCourses;
    }
    if (activeCourse === "Assigned Courses") {
      return instructorCourses;
    }
    if (activeCourse === "Draft Courses") {
      return instructorDraftCourses;
    }
    if (activeCourse === "Unpublished Courses") {
      return instructorUnpublisCourses;
    }
    if (activeCourse === "Declined Courses") {
      return instructorDeclineCourses;
    }
    return [];
  }, [activeCourse, allCourses, instructorCourses]);

  return (
    <>
      {isLoading && (
        <div className="loader-container">
          <div className="dottedLoading theme-color"></div>
        </div>
      )}
      <div className="instructor-courses">
        {/* Top Bar */}
        <div className="topbar mb-20 mb-lg-36">
          <div className="d-flex align-items-center justify-content-between">
            <h3 className="ad-heading-04 fw-bold mb-8">All Courses</h3>
            <div className="search-wrapper d-none d-lg-flex align-items-center gap-3">
              <DashboardTopSearch />
              <InstructorNotification />
            </div>
          </div>
        </div>

        {/* Course Content */}
        <div className="card-default-style py-42">
          <div className="row justify-content-center">
            <div className="col-12 col-md-10 col-lg-8 col-xl-6 text-center">
              <h1 className="fw-bold ah-heading-03 mb-12">Courses</h1>
              <p className="fw-semibold mb-lg-32 text-gray-01">
                Dive into our extensive and diverse range of courses,
                meticulously designed to inspire and empower you to learn
                something new and exciting every single day!
              </p>
            </div>
          </div>

          {/* Course Filters and List */}
          <div className="row">
            <div className="col-12 text-center">
              <TabFilterCourse
                categories={COURSE_CATEGORIES}
                activeCourse={activeCourse}
                setActiveCourse={setActiveCourse}
              />
            </div>
            <div className="col-12">
              {isLoading ? (
                <p>Loading courses...</p>
              ) : error ? (
                <p className="text-danger">{error}</p>
              ) : (
                <div className="row course-item-list">
                  <TabFilterContentCourse data={filteredCard} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
